import React from "react";
import { Check } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const packages = [{
  name: "Starter",
  price: "$995",
  gst: "+ GST",
  hosting: "$39 per month + GST",
  description: "Perfect for new businesses needing a professional online presence.",
  features: ["Custom modern website design", "12 months hosting included", "12 months domain registration", "Up to 3 pages", "Mobile responsive design", "Basic SEO optimisation", "Contact form integration", "Click-to-call buttons", "Ongoing updates (2/month)", "Unlimited technical calls", "Two rounds of design revisions", "One round of content revisions"],
  cta: "Learn More",
  link: "/StarterPackagePage",
  popular: false
}, {
  name: "Advanced",
  price: "$1400",
  gst: "+ GST",
  hosting: "$39 per month + GST",
  description: "Ideal for growing businesses requiring more content and flexibility.",
  features: ["Custom modern website design", "12 months hosting included", "12 months domain registration", "Up to 5 pages", "1-hour branding session", "Mobile responsive design", "Basic SEO optimisation", "Contact form integration", "Click-to-call buttons", "Ongoing updates (5/month)", "Unlimited technical calls"],
  cta: "Learn More",
  link: "/AdvancedPackagePage",
  popular: true
}];
export default function TransparentGrowthTiers() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="transparent-growth-tiers" ref={ref} className={cn("relative w-full py-24 overflow-hidden bg-background text-foreground transition-all duration-700", isVisible ? "opacity-100" : "opacity-0")}>
      {}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[140%] h-[80%] bg-primary/5 blur-[120px] rounded-full opacity-40" />
      </div>

      <div className="container relative mx-auto px-4 md:px-6">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
            Transparent Growth Tiers
          </h2>
          <p className="text-muted-foreground text-lg">
            Choose the perfect package to launch or grow your business online. 
            No hidden fees, just clear value.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center max-w-5xl mx-auto">
          {packages.map((pkg, index) => <div key={pkg.name} data-index={index} className={cn("relative transition-all duration-300", pkg.popular ? "lg:scale-105 z-10" : "z-0")}>
              {pkg.popular && <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20">
                  <Badge className="bg-primary text-primary-foreground hover:bg-primary px-4 py-1 text-sm uppercase tracking-wider font-bold shadow-lg shadow-primary/20">
                    Most Popular
                  </Badge>
                </div>}

              <Card className={cn("h-full flex flex-col border-border/50 bg-card/50 backdrop-blur-sm shadow-2xl", pkg.popular ? "border-primary/30 shadow-primary/10 ring-1 ring-primary/20" : "hover:border-primary/20 hover:shadow-lg transition-colors")}>
                <CardHeader className="pb-8 text-center space-y-4">
                  <CardTitle className="text-2xl font-medium text-muted-foreground">
                    {pkg.name}
                  </CardTitle>
                  <div className="flex flex-col items-center justify-center gap-1">
                    <div className="flex items-baseline gap-1">
                      <span className="text-5xl md:text-6xl font-bold tracking-tighter text-foreground">
                        {pkg.price}
                      </span>
                      <span className="text-xl text-muted-foreground font-medium">
                        {pkg.gst}
                      </span>
                    </div>
                    <p className="text-sm text-primary font-medium">
                      Hosting: {pkg.hosting}
                    </p>
                  </div>
                  <CardDescription className="text-base max-w-xs mx-auto">
                    {pkg.description}
                  </CardDescription>
                </CardHeader>

                <CardContent className="flex-1">
                  <div className="space-y-4">
                    <div className="h-px w-full bg-border/50 mb-6" />
                    <ul className="space-y-3">
                      {pkg.features.map((feature, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                          <div className="mt-1 shrink-0 rounded-full bg-primary/10 p-1">
                            <Check className="h-3.5 w-3.5 text-primary" />
                          </div>
                          <span className="text-sm md:text-base text-muted-foreground leading-tight">
                            {feature}
                          </span>
                        </li>)}
                    </ul>
                  </div>
                </CardContent>

                <CardFooter className="pt-8 pb-8">
                  <Button asChild className={cn("w-full rounded-full h-12 text-base font-semibold shadow-lg transition-all duration-300", pkg.popular ? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-primary/25 hover:shadow-primary/40" : "bg-secondary text-secondary-foreground hover:bg-secondary/80")}>
                    <Link to={pkg.link} className="inline-flex items-center justify-center gap-2 whitespace-nowrap [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive px-4 py-2 has-[>svg]:px-3 w-full rounded-full h-12 text-base font-semibold shadow-lg transition-all duration-300 bg-primary shadow-primary/25 !text-[hsl(0,0%,100%)] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-primary/90 hover:shadow-primary/40">{pkg.cta}</Link>
                  </Button>
                </CardFooter>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}